/**
 * ImageZoom — pinch, drag and double-tap zoom for a single image.
 *
 * Built for the full-screen manga viewer: "full screen mangas view should
 * allow zooming". The browser's own pinch zooms the whole PAGE, which on a
 * `position: fixed` overlay scales the chrome along with the art and leaves
 * the player panning a viewport rather than a panel. This zooms the image
 * itself, so the overlay stays put and the panel is what moves.
 *
 * Deliberately small and self-contained: it owns nothing but the transform of
 * the element it is given, and `reset()` returns that element to untouched.
 */
import { uiPx } from './ui-scale.js';

let ImageZoom = {};

(function initImageZoom() {
  const MIN_SCALE = 1;
  const MAX_SCALE = 4;
  const DOUBLE_TAP_SCALE = 2.5;
  const DOUBLE_TAP_MS = 300;
  const DOUBLE_TAP_SLOP = 30;

  const states = new WeakMap();

  function clamp(value, low, high) {
    return Math.max(low, Math.min(value, high));
  }

  /** Keep the image overlapping its own box: no panning it off screen. */
  function clampOffset(state) {
    const rect = state.el.getBoundingClientRect();
    const baseW = rect.width / state.scale;
    const baseH = rect.height / state.scale;
    const maxX = Math.max(0, (baseW * state.scale - baseW) / 2);
    const maxY = Math.max(0, (baseH * state.scale - baseH) / 2);
    state.x = clamp(state.x, -maxX, maxX);
    state.y = clamp(state.y, -maxY, maxY);
  }

  function apply(state) {
    clampOffset(state);
    state.el.style.transform = state.scale === 1 && state.x === 0 && state.y === 0
      ? ''
      : `translate(${state.x}px, ${state.y}px) scale(${state.scale})`;
    state.el.classList.toggle('is-zoomed', state.scale > 1);
  }

  function distance(a, b) {
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  function midpoint(a, b) {
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  }

  function pointerList(state) {
    return [...state.pointers.values()];
  }

  function onPointerDown(state, event) {
    state.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (state.pointers.size === 2) {
      const [a, b] = pointerList(state);
      state.pinchStart = { distance: distance(a, b), scale: state.scale, centre: midpoint(a, b) };
    }
    if (state.pointers.size === 1) {
      state.panStart = { x: event.clientX, y: event.clientY, ox: state.x, oy: state.y };
      const now = state.now();
      const near = state.lastTap
        && now - state.lastTap.at < DOUBLE_TAP_MS
        && Math.hypot(event.clientX - state.lastTap.x, event.clientY - state.lastTap.y) < uiPx(DOUBLE_TAP_SLOP);
      if (near) {
        state.lastTap = null;
        toggleDoubleTap(state, event);
      } else {
        state.lastTap = { at: now, x: event.clientX, y: event.clientY };
      }
    }
    // Only claim the gesture once it is ours to claim: a single finger at rest
    // scale must still be able to close the overlay by tapping the backdrop.
    if (state.scale > 1 || state.pointers.size > 1) {
      try { state.el.setPointerCapture(event.pointerId); } catch (_) { /* fine */ }
    }
  }

  function toggleDoubleTap(state, event) {
    if (state.scale > 1) {
      state.scale = 1;
      state.x = 0;
      state.y = 0;
    } else {
      const rect = state.el.getBoundingClientRect();
      const cx = event.clientX - (rect.left + rect.width / 2);
      const cy = event.clientY - (rect.top + rect.height / 2);
      state.scale = DOUBLE_TAP_SCALE;
      // Pull the tapped point toward the centre so it stays under the finger.
      state.x = -cx * (DOUBLE_TAP_SCALE - 1);
      state.y = -cy * (DOUBLE_TAP_SCALE - 1);
    }
    apply(state);
  }

  function onPointerMove(state, event) {
    if (!state.pointers.has(event.pointerId)) return;
    state.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (state.pointers.size >= 2 && state.pinchStart) {
      const [a, b] = pointerList(state);
      const spread = distance(a, b);
      if (state.pinchStart.distance > 0) {
        state.scale = clamp(
          state.pinchStart.scale * (spread / state.pinchStart.distance),
          MIN_SCALE,
          MAX_SCALE,
        );
      }
      apply(state);
      return;
    }

    if (state.pointers.size === 1 && state.panStart && state.scale > 1) {
      state.x = state.panStart.ox + (event.clientX - state.panStart.x);
      state.y = state.panStart.oy + (event.clientY - state.panStart.y);
      apply(state);
    }
  }

  function onPointerEnd(state, event) {
    state.pointers.delete(event.pointerId);
    if (state.pointers.size < 2) state.pinchStart = null;
    if (state.pointers.size === 0) {
      state.panStart = null;
      // A pinch that ended below 1 springs back rather than leaving a gap.
      if (state.scale <= MIN_SCALE) {
        state.scale = MIN_SCALE;
        state.x = 0;
        state.y = 0;
        apply(state);
      }
    }
  }

  function attach(el, options) {
    if (!el || states.has(el)) return false;
    const state = {
      el,
      scale: 1,
      x: 0,
      y: 0,
      pointers: new Map(),
      pinchStart: null,
      panStart: null,
      lastTap: null,
      now: (options && typeof options.now === 'function')
        ? options.now
        : () => Date.now(),
    };
    state.handlers = {
      pointerdown: (e) => onPointerDown(state, e),
      pointermove: (e) => onPointerMove(state, e),
      pointerup: (e) => onPointerEnd(state, e),
      pointercancel: (e) => onPointerEnd(state, e),
    };
    Object.entries(state.handlers).forEach(([type, fn]) => el.addEventListener(type, fn));
    states.set(el, state);
    el.classList.add('is-zoomable');
    return true;
  }

  function isZoomed(el) {
    const state = states.get(el);
    return !!state && state.scale > 1;
  }

  function reset(el) {
    const state = states.get(el);
    if (!state) return false;
    state.scale = 1;
    state.x = 0;
    state.y = 0;
    state.pointers.clear();
    state.pinchStart = null;
    state.panStart = null;
    state.lastTap = null;
    apply(state);
    return true;
  }

  function detach(el) {
    const state = states.get(el);
    if (!state) return false;
    Object.entries(state.handlers).forEach(([type, fn]) => el.removeEventListener(type, fn));
    el.style.transform = '';
    el.classList.remove('is-zoomable', 'is-zoomed');
    states.delete(el);
    return true;
  }

  ImageZoom = { attach, reset, detach, isZoomed };
})();

export { ImageZoom };
